<template>
  <question-base-wrapper
    :question-id="questionId || 'period-of-licence-held'"
    :title="questionLabel"
    v-bind="baseWrapperProps"
  >
    <validation-provider
      v-slot="{ errors }"
      :vid="`${stepId}_period-of-licence-held_years`"
      name="Licence Years"
      rules="listOptionRequired"
    >
      <qz-select
        v-model="licence.held.years"
        :data-test="fieldTrackerId.years"
        :items="licenceYearsHeldItems"
        :error-messages="errors"
        :placeholder="helpers.getPlaceholderResponsiveText('Please select years', isMobile)"
      />
    </validation-provider>
    <form-row v-if="licence.held.years !== null && +licence.held.years < 4">
      <validation-provider
        v-slot="{ errors }"
        :vid="`${stepId}_period-of-licence-held_months`"
        name="Licence Months"
        rules="listOptionRequired"
      >
        <qz-select
          v-model="licence.held.months"
          :data-test="fieldTrackerId.months"
          :items="licenceMonthsHeld"
          :error-messages="errors"
          :placeholder="helpers.getPlaceholderResponsiveText('Please select months', isMobile)"
        />
      </validation-provider>
    </form-row>
    <validation-provider
      v-slot="{ failed, errors }"
      :vid="questionId
        ? `${stepId}_${questionId}`
        :`${stepId}_period-of-licence-held`"
      name="Period Of Licence Held"
      immediate
      :rules="{
        [ruleName]: computedLicenceHeldRules,
      }"
    >
      <input
        type="hidden"
        :value="questionData[questionFields.periodOfLicenceHeld]"
      >
      <div
        v-if="
          showPeriodOfLicenceError &&
            failed &&
            isPeriodOfLicenceHeldDateError
        "
        class="error--text mt-3 mb-3"
      >
        <p>{{ errors[0] }}</p>
      </div>
    </validation-provider>
  </question-base-wrapper>
</template>

<script>
import { ExtDate, UtcDate } from '@/helpers/date';
import QuestionBase from './Base';
import {
  range,
  snakeCase,
} from 'lodash';
import pluralize from 'pluralize';

export default {
  name: 'question-period-of-licence-held',
  extends: QuestionBase,
  inject: ['constants'],
  data () {
    return {
      fields: ['periodOfLicenceHeld'],
      licence: {
        held: {
          years: null,
          months: null,
        },
      },
    };
  },
  computed: {
    ruleName () {
      // We only want the bike validation for the bike licence question
      // For the car licence question use the car rule.
      return this.productName === 'bike' && this.questionId !== 'car-period-of-licence-held' ? 'bikePeriodOfLicenceHeld' : 'periodOfLicenceHeld';
    },
    computedLicenceHeldRules () {
      const rules = {
        dateOfBirth: this.stepData?.dateOfBirth,
      };

      if (this.productName === 'bike' && this.questionId === 'car-period-of-licence-held') {
        rules.typeOfDriving = this.stepData?.carTypeOfDriving;
        rules.driverTypeOfDriving = this.constants.DRIVER_CAR_TYPE_OF_DRIVING;
      } else {
        rules.typeOfDriving = this.stepData?.typeOfDriving;
        rules.driverTypeOfDriving = this.constants.DRIVER_TYPE_OF_DRIVING;
      }

      // if typeOfDriving is provisional
      if (this.productName === 'bike' && this.stepData?.typeOfDriving === 'P' && this.questionId === 'period-of-licence-held') {
        rules.licenceObtainMinAge = this.constants.PROVISIONAL_LICENCE_OBTAIN_MIN_AGE;
      }

      return rules;
    },
    questionLabel () {
      if (!this.isAdditional) {
        return this.title || 'How many years have you held your licence?';
      }
      return this.titleAdditional || this.title || 'How many years have they held their licence?';
    },
    licenceYearsHeldItems () {
      if (['car', 'motorhome', 'caravan'].includes(this.productName)) {
        const optionsRage = range(this.optionsConfig?.optionsAmount);
        const options = optionsRage.map(
          (item, index) => {
            let text = `${index} ${pluralize('Year', index)}`;

            if (index === 0) text = 'Less than 1 Year';
            if (index === optionsRage.length - 1) text = `${index} Years or more`;

            return {
              text,
              value: index,
            };
          },
        );

        return this.helpers.createOptions({
          source: options,
          asNumbers: true,
        });
      }

      return this.helpers.createOptions({
        source: this.constants.DRIVER_LICENCE_YEARS_HELD,
        asNumbers: true,
      });
    },
    licenceMonthsHeld () {
      return this.helpers.createOptions({
        source: this.constants.DRIVER_LICENCE_MONTHS_HELD,
        asNumbers: true,
      });
    },
    isPeriodOfLicenceHeldDateError () {
      return this.formObserver.validated ||
        (this.questionData[this.questionFields.periodOfLicenceHeld] && new ExtDate(this.questionData[this.questionFields.periodOfLicenceHeld]).isValid());
    },
    showPeriodOfLicenceError () {
      return this.licence.held.years !== null && (this.licence.held.years < 4 ? this.licence.held.months !== null : true);
    },
    fieldTrackerId () {
      return {
        years: `${snakeCase(this.questionFields.periodOfLicenceHeld)}_years`,
        months: `${snakeCase(this.questionFields.periodOfLicenceHeld)}_months`,
      };
    },
  },
  created () {
    const periodOfLicenceHeld = this.questionData[this.questionFields.periodOfLicenceHeld];
    const todayUtc = new UtcDate();

    if (periodOfLicenceHeld) {
      const licenceHeldDate = new UtcDate(periodOfLicenceHeld);
      this.licence.held.years = todayUtc.differenceInYears(licenceHeldDate);
      this.licence.held.months = todayUtc.differenceInMonths(licenceHeldDate) % 12;
    }

    this.$watch('licence.held', (val) => {
      if (val.years === null || (val.years < 4 ? val.months === null : false)) {
        this.questionData[this.questionFields.periodOfLicenceHeld] = null;
        return;
      }

      this.questionData[this.questionFields.periodOfLicenceHeld] =
        new UtcDate().sub({
          years: val.years || 0,
          months: val.years < 4 ? (val.months || 0) : 0,
        }).format();
    }, { deep: true });
  },
  watch: {
    shouldQuestionRender (val) {
      if (!val) {
        this.licence.held.years = null;
        this.licence.held.months = null;
      }
    },
  },
};
</script>
